export const ESIGN_TEXT_FONT_SIZES = [8, 9, 10, 11, 12, 14, 16, 18] as const;

export const ESIGN_TEXT_DEFAULT_FONT_SIZE = 12;

/** Line box height relative to the font size. Matches the PDF stamp. */
export const ESIGN_TEXT_LINE_RATIO = 1.2;

/** US Letter height, used when the rendered PDF page size is not known yet. */
export const ESIGN_TEXT_PAGE_HEIGHT_PT = 792;
/** US Letter width, used for sizing date fields before the PDF page is loaded. */
export const ESIGN_TEXT_PAGE_WIDTH_PT = 612;

export type EsignTextFontSize = (typeof ESIGN_TEXT_FONT_SIZES)[number];

const LINED_TEXT_FIELD_TYPES = new Set([
  "TEXT",
  "DATE",
  "SIGNATURE_DATE",
  "COMPLETION_DATE",
]);

/** Text and date fields are sized and filled in whole lines. */
export function isLinedTextFieldType(type: string): boolean {
  return LINED_TEXT_FIELD_TYPES.has(type);
}

export function normalizeEsignFontSize(
  value: number | null | undefined,
): EsignTextFontSize {
  if (
    value != null &&
    (ESIGN_TEXT_FONT_SIZES as readonly number[]).includes(value)
  ) {
    return value as EsignTextFontSize;
  }
  return ESIGN_TEXT_DEFAULT_FONT_SIZE;
}

export function textLineHeightPct(
  fontSize: number,
  pageHeightPt: number,
): number {
  const page = pageHeightPt > 0 ? pageHeightPt : ESIGN_TEXT_PAGE_HEIGHT_PT;
  return ((fontSize * ESIGN_TEXT_LINE_RATIO) / page) * 100;
}

export function textLineCount(
  heightPct: number,
  fontSize: number,
  pageHeightPt: number,
): number {
  const line = textLineHeightPct(fontSize, pageHeightPt);
  if (line <= 0) return 1;
  return Math.max(1, Math.round(heightPct / line));
}

export function textHeightForLines(
  lineCount: number,
  fontSize: number,
  pageHeightPt: number,
): number {
  return Math.max(1, lineCount) * textLineHeightPct(fontSize, pageHeightPt);
}

/**
 * Minimum width for a date field that can display its complete example at the
 * selected font size. The small safety margin accounts for font metric
 * differences between the browser and the embedded PDF font.
 */
export function dateFieldMinWidthPct(
  dateExample: string,
  fontSize: number,
  pageWidthPt = ESIGN_TEXT_PAGE_WIDTH_PT,
): number {
  const estimatedTextWidth = dateExample.length * fontSize * 0.56 + 6;
  return Math.min(
    100,
    Math.max(1, (estimatedTextWidth / pageWidthPt) * 100 * 1.1),
  );
}

/** Snap a text-field height to the nearest whole line, keeping at least one. */
export function snapTextFieldHeight(
  heightPct: number,
  fontSize: number,
  pageHeightPt: number,
): number {
  return textHeightForLines(
    textLineCount(heightPct, fontSize, pageHeightPt),
    fontSize,
    pageHeightPt,
  );
}
